import classNames from 'classnames'
import { format } from 'date-fns'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Chart, ChartDataItem } from '@ui/components/Chart/Chart'

import { useGAEvents } from '@utils/callGAEvents'
import { useWindowResize } from '@utils/domUtils'

import {
  IntervalType,
  IntroducingBrokerPerformanceChartDetailDto,
} from '@model/IntroducingBrokerPerformance'

import { HorizontalScroll } from '@global/HorizontalScroll/HorizontalScroll'
import { dateFnsLocales } from '@global/locale/Locale'

import { Box } from '@shared/components/Box/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { SectionHeader } from '../components/SectionHeader/SectionHeader'
import { DateFilter } from './DateFilter'
import { PerformanceType, TypeFilter } from './TypeFilter'

import styles from './QuickPerformance.module.scss'

interface QuickPerformanceProps {
  isLoading: boolean
  className?: string
  ibPerformanceData: IntroducingBrokerPerformanceChartDetailDto[]
  dateRange: [Date, Date]
  setDateRange: (dateRange: [Date, Date]) => void
  isResizable?: boolean
}

export const QuickPerformance: FC<QuickPerformanceProps> = (props) => {
  const { isLoading, className, ibPerformanceData, dateRange, setDateRange, isResizable } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const locale = useSessionLanguage()
  const { callGAEvents } = useGAEvents()

  const [performanceType, setPerformanceType] = useState<PerformanceType>(
    PerformanceType.Commission
  )

  const getValueByPerformanceType = useCallback(
    (item: IntroducingBrokerPerformanceChartDetailDto): number => {
      switch (performanceType) {
        case PerformanceType.Commission:
          return item.commission
        case PerformanceType.Lots:
          return item.lots
        case PerformanceType.Clients:
          return item.clientsCount
        default:
          return 0
      }
    },
    [performanceType]
  )

  const chartData = useMemo<ChartDataItem[]>(() => {
    return ibPerformanceData.map((item, index) => {
      const date = index === 0 ? item.startDate : item.endDate

      const shortFormat = item.intervalType.id === IntervalType.Daily ? 'd MMM' : 'MMM yy'
      const longFormat = item.intervalType.id === IntervalType.Daily ? 'd MMMM' : 'MMMM yyyy'

      const name = format(new Date(date), shortFormat, { locale: dateFnsLocales[locale] })
      const tooltipLabel = format(new Date(date), longFormat, { locale: dateFnsLocales[locale] })

      return {
        name: name.toUpperCase(),
        tooltipLabel: tooltipLabel.toUpperCase(),
        value: getValueByPerformanceType(item),
      }
    })
  }, [ibPerformanceData, getValueByPerformanceType, locale])

  return (
    <div className={classNames(styles.quickPerformance, className)}>
      <SensitiveData
        style={
          isResizable ? { height: '100%', display: 'flex', flexDirection: 'column' } : undefined
        }
      >
        <SectionHeader
          title={t('IB.Dashboard.Quick performance')}
          seeMoreLinkProps={{
            onClick: () => callGAEvents().IBDashboard.QuickPerformance.seeMore(),
            to: '/dashboard/introducing-broker/reports/ib-reports',
          }}
          iconMobileOnly
        />
        <div className={styles.filters}>
          <TypeFilter activeTab={performanceType} onTabChanged={setPerformanceType} />
          {!isMobile && <DateFilter dateRange={dateRange} onDateRangeSelect={setDateRange} />}
        </div>
        <div className={isResizable ? styles.resizableChart : styles.chart}>
          <Loading isLoading={isLoading} showLoadingIcon>
            <Chart data={chartData} performanceType={performanceType} />
          </Loading>
        </div>
        {isMobile && (
          <Box p='x16'>
            <HorizontalScroll>
              <DateFilter dateRange={dateRange} onDateRangeSelect={setDateRange} />
            </HorizontalScroll>
          </Box>
        )}
      </SensitiveData>
    </div>
  )
}
